/**
 * Foundation tokens — raw, context-free design values.
 *
 * These are the single source of truth for colors, spacing, type scale,
 * typography, radii, motion, elevation, and z-index. They are not consumed
 * directly by components; semantic tokens and component overrides derive from
 * them.
 */

export const palette = {
  // Primary — deep institutional blue, distinctive from MUI defaults
  primary: {
    50: '#e6f0f7',
    100: '#c2d9eb',
    200: '#99bfde',
    300: '#70a5d1',
    400: '#4d8fc7',
    500: '#1e5a8e',
    600: '#1a4f7d',
    700: '#15426a',
    800: '#103556',
    900: '#0a2840',
  },
  // Neutral greys used for surfaces, text, and borders
  neutral: {
    0: '#ffffff',
    25: '#fafbfc',
    50: '#f5f7fa',
    100: '#eef1f5',
    200: '#e3e7ec',
    300: '#cdd3db',
    400: '#9ea6b3',
    500: '#6e7887',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
  // Semantic functional colors — unified temperature and saturation
  success: { light: '#dcfce7', main: '#16a34a', dark: '#15803d' },
  warning: { light: '#fef3c7', main: '#d97706', dark: '#b45309' },
  error: { light: '#fee2e2', main: '#dc2626', dark: '#b91c1c' },
  info: { light: '#dbeafe', main: '#2563eb', dark: '#1d4ed8' },
} as const;

/**
 * A deliberate, zero-download system stack avoids rendering a declared web
 * font that the application never loads. It preserves native platform quality,
 * privacy, CSP compatibility, and fast first paint.
 */
export const fontFamilies = {
  sans: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
} as const;

export const typeScale = {
  display: { fontSize: '2rem', lineHeight: 1.2, fontWeight: 700 },
  pageTitle: { fontSize: '1.75rem', lineHeight: 1.25, fontWeight: 700 },
  sectionTitle: { fontSize: '1.25rem', lineHeight: 1.35, fontWeight: 600 },
  panelTitle: { fontSize: '1.125rem', lineHeight: 1.4, fontWeight: 600 },
  subsectionTitle: { fontSize: '1.0625rem', lineHeight: 1.4, fontWeight: 600 },
  compactTitle: { fontSize: '1rem', lineHeight: 1.4, fontWeight: 600 },
  body: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 400 },
  supporting: { fontSize: '0.875rem', lineHeight: 1.43, fontWeight: 400 },
  metadata: { fontSize: '0.75rem', lineHeight: 1.33, fontWeight: 400 },
  overline: { fontSize: '0.75rem', lineHeight: 1.33, fontWeight: 600, letterSpacing: '0.08em' },
} as const;

export const spacing = {
  0: 0,
  0.5: 4,
  1: 8,
  1.5: 12,
  2: 16,
  2.5: 20,
  3: 24,
  4: 32,
  5: 40,
  6: 48,
  8: 64,
  10: 80,
  12: 96,
} as const;

export const radii = {
  none: 0,
  small: 6,
  medium: 8,
  large: 12,
  pill: 9999,
} as const;

export const motion = {
  duration: {
    instant: '0ms',
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
  },
  easing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    enter: 'cubic-bezier(0, 0, 0.2, 1)',
    exit: 'cubic-bezier(0.4, 0, 1, 1)',
  },
} as const;

export const shadows = {
  none: 'none',
  surface: '0 1px 3px 0 rgba(0, 0, 0, 0.08)',
  raised: '0 8px 24px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.06)',
  overlay: '0 20px 32px -8px rgba(0, 0, 0, 0.14), 0 8px 16px -4px rgba(0, 0, 0, 0.08)',
} as const;

export const zIndex = {
  sticky: 1000,
  header: 1100,
  drawer: 1200,
  fab: 1050,
  modal: 1300,
  commandPalette: 1400,
  tooltip: 1500,
} as const;
